import { useState, useCallback, useRef } from 'react';
import { useToast } from '@practium/ui';
import { useAuth } from '@practium/features/auth';
import type { SoilAnalysisFormData } from '../types/analyses.types';
import { SoilAnalysisSchema } from '../schemas/analyses.schema';
import { DEFAULT_SOIL_FORM } from '../constants/analyses.constants';
import { mapSoilFormToCreatePayload } from '../utils/analysisApiMappers';
import { usePostSoilAnalysis, useUpdateSoilAnalysis } from '@practium/features/api';

export interface UseSoilAnalysisFormOptions {
	initialData?: SoilAnalysisFormData;
	analysisId?: string;
	onSubmit?: () => void;
	onLoadingChange?: (loading: boolean) => void;
}

export const useSoilAnalysisForm = ({
	initialData,
	analysisId,
	onSubmit,
	onLoadingChange,
}: UseSoilAnalysisFormOptions) => {
	const toast = useToast();
	const { user } = useAuth();
	const initialForm = initialData ? { ...initialData } : { ...DEFAULT_SOIL_FORM };
	const [form, setForm] = useState<SoilAnalysisFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isLoading, setIsLoading] = useState(false);
	const { mutateAsync: postSoilAnalysis } = usePostSoilAnalysis();
	const { mutateAsync: updateSoilAnalysis } = useUpdateSoilAnalysis();

	const set = useCallback(
		<K extends keyof SoilAnalysisFormData>(field: K, value: SoilAnalysisFormData[K]) => {
			setForm((prev) => ({ ...prev, [field]: value }));
			setErrors((prev) => {
				if (!prev[field]) return prev;
				const next = { ...prev };
				delete next[field as string];
				return next;
			});
		},
		[]
	);

	const populate = useCallback((data: SoilAnalysisFormData) => {
		setForm({ ...data });
		setErrors({});
	}, []);

	const isDirty = useCallback(
		() => JSON.stringify(form) !== JSON.stringify(initialFormRef.current),
		[form]
	);

	const syncBaselineField = useCallback(
		<K extends keyof SoilAnalysisFormData>(field: K, value: SoilAnalysisFormData[K]) => {
			initialFormRef.current = { ...initialFormRef.current, [field]: value };
		},
		[]
	);

	const reset = useCallback(() => {
		setForm(initialData ? { ...initialData } : { ...DEFAULT_SOIL_FORM });
		setErrors({});
	}, [initialData]);

	const handleSubmit = useCallback(
		async (options?: { showSuccessToast?: boolean }) => {
			const showSuccessToast = options?.showSuccessToast ?? true;
			const result = SoilAnalysisSchema.safeParse(form);
			if (!result.success) {
				const fieldErrors: Record<string, string> = {};
				for (const issue of result.error.issues) {
					const key = issue.path[0] as string;
					if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
				}
				setErrors(fieldErrors);
				toast.error('Corrija os campos destacados antes de salvar.');
				return false;
			}
			if (!user?._id) {
				toast.error('Usuário autenticado não encontrado.');
				return false;
			}

			setIsLoading(true);
			onLoadingChange?.(true);
			try {
				const payload = mapSoilFormToCreatePayload(form, user._id);
				if (analysisId) {
					await updateSoilAnalysis({ id: analysisId, data: payload });
				} else {
					await postSoilAnalysis(payload);
				}
				if (showSuccessToast) {
					toast.success(
						analysisId
							? 'Análise de solo atualizada com sucesso!'
							: 'Análise de solo salva com sucesso!'
					);
				}
				if (!analysisId) reset();
				onSubmit?.();
				return true;
			} catch (err) {
				const msg = err instanceof Error ? err.message : 'Erro ao salvar análise de solo';
				toast.error(msg);
				return false;
			} finally {
				setIsLoading(false);
				onLoadingChange?.(false);
			}
		},
		[
			form,
			user?._id,
			toast,
			analysisId,
			updateSoilAnalysis,
			postSoilAnalysis,
			reset,
			onSubmit,
			onLoadingChange,
		]
	);

	return { form, errors, isLoading, set, populate, reset, handleSubmit, isDirty, syncBaselineField };
};
